import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import HousingMetricSelector from '../selectors/HousingMetricSelector';

import { housingPermitsData } from '../api/housingData';

const HousingCharts = () => {
  const [rawData] = useState(housingPermitsData);
  const [selectedView, setSelectedView] = useState('us');

  // Define state names for tooltip
  const stateNames = {
    CT: "Connecticut",
    US: "United States",
    MA: "Massachusetts",
    ME: "Maine",
    NH: "New Hampshire",
    RI: "Rhode Island",
    VT: "Vermont"
  };

  // Define colors for different states
  const stateColors = {
    CT: "#1f77b4",  // blue
    US: "#2ca02c",  // green
    MA: "#ff7f0e",  // orange
    ME: "#d62728",  // red
    NH: "#9467bd",  // purple
    RI: "#8c564b",  // brown
    VT: "#e377c2"   // pink
  };

  // Transform and sort the data
  const processedData = useMemo(() => {
    // Group by month
    const groupedByMonth = rawData.reduce((acc, item) => {
      const { month, state, value } = item;
      if (!acc[month]) {
        acc[month] = { month };
      }
      acc[month][state] = parseFloat(value);
      return acc;
    }, {});

    // Convert to array and sort by month
    return Object.values(groupedByMonth)
      .sort((a, b) => new Date(a.month) - new Date(b.month));
  }, [rawData]);

  // Get filtered states based on selected view
  const filteredStates = useMemo(() => {
    const baseStates = selectedView === 'us' ? ['CT', 'US'] : ['CT', 'MA', 'ME', 'NH', 'RI', 'VT'];
    
    // Get the most recent month's data
    const latestMonth = processedData[processedData.length - 1];
    
    // Sort states by their values in descending order
    return baseStates.sort((a, b) => {
      const valueA = latestMonth?.[a] || 0;
      const valueB = latestMonth?.[b] || 0;
      return valueB - valueA;
    });
  }, [selectedView, processedData]);

  // Add parseDate function
  const parseDate = (dateString) => {
    const [year, month] = dateString.split('-');
    const date = new Date(year, month - 2);
    // Add one month to correct the display
    date.setMonth(date.getMonth() + 1);
    return date;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    // Sort payload by value in descending order
    const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

    return (
      <div className="bg-white p-4 border rounded shadow">
        <p className="font-semibold mb-2">
          {parseDate(label).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
        {sortedPayload.map((entry, index) => (
          <div 
            key={index} 
            className="flex items-center gap-2 text-sm"
            style={{ color: entry.color }}
          >
            <div className="font-medium">
              {stateNames[entry.name]}:
            </div>
            <div>
              {entry.value?.toFixed(2)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Housing Permits per 10k Residents, 12 Month Moving Average</h3>
      <HousingMetricSelector selectedView={selectedView} setSelectedView={setSelectedView} />
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={processedData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis 
            dataKey="month" 
            tickFormatter={(tick) => {
              const date = parseDate(tick);
              return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            }}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend formatter={(value) => stateNames[value]} />
          {filteredStates.map((state) => (
            <Line
              key={state}
              type="monotone"
              dataKey={state}
              stroke={stateColors[state]}
              name={state}
              dot={false}
              strokeWidth={state === 'CT' ? 2.5 : 1.5}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default HousingCharts;